import { ContentResponse } from "@authory-packages/core";
import { V3BodyGrey48 } from "../../ReusableStyledComponents/V3BodyGrey48";
import { V3ItemPreview } from "../../V3ItemPreview";
import { VerticalSpacing } from "../../VerticalSpacing";
import { RoundedBoxCTA } from "../SharedComponents/RoundedBoxCTA";
import { RightAlignPanel } from "../../ReusableStyledComponents";
import { ModalLoadingSpinner } from "../../ReusableStyledComponents/ModalLoadingSpinner";
import { V3Button, V3ButtonColor } from "../../Button";
import { useState } from "react";
import { getImportContentCopyLabel } from "./AddContent.utils";
import { InlineLink } from "../../ReusableStyledComponents/InlineLink";

interface DuplicateCheckProps {
    /** The item already in the account that the incoming content looks like */
    item: ContentResponse;
    /** Url of the content being imported, so the user can open it and compare. Undefined for file uploads, which have no url. */
    importUrl?: string | null;
    isPlugin?: boolean;
    onReplace: () => Promise<void> | void;
    onAddAsNew: () => Promise<void> | void;
    onCloseHandler: () => void;
    /** Leaves the existing item untouched and moves on to the next item being imported. Omitted when there is no next item, as in the plugin. */
    onSkip?: () => void;
}

export const DuplicateCheck = ({ item, importUrl, isPlugin = false, onReplace, onAddAsNew, onCloseHandler, onSkip }: DuplicateCheckProps) => {
    const [modalSubmitting, setModalSubmitting] = useState(false);

    const label = getImportContentCopyLabel(item.type);

    const runAction = async (action: () => Promise<void> | void) => {
        if (modalSubmitting) return;

        setModalSubmitting(true);

        try {
            await action();
        } finally {
            setModalSubmitting(false);
        }
    };

    return (
        <>
            <VerticalSpacing bottom={18}>
                <V3BodyGrey48>
                    {importUrl ? (
                        <InlineLink href={importUrl} target="_blank">
                            This {label}
                        </InlineLink>
                    ) : (
                        <>This {label}</>
                    )}{" "}
                    looks very similar to one already in your account:
                </V3BodyGrey48>
            </VerticalSpacing>
            <VerticalSpacing bottom={18}>
                <V3ItemPreview item={item} target="_blank" skipUrl={isPlugin} />
            </VerticalSpacing>
            <VerticalSpacing bottom={18}>
                <V3BodyGrey48>You have the following options:</V3BodyGrey48>
            </VerticalSpacing>
            <RoundedBoxCTA text={`Replace the existing ${label}`} disabled={modalSubmitting} onClick={() => runAction(onReplace)} />
            <RoundedBoxCTA text={`Add as a new ${label}`} disabled={modalSubmitting} onClick={() => runAction(onAddAsNew)} />
            <VerticalSpacing top={18}>
                <V3BodyGrey48>
                    If you don't want to replace the existing {label} or add the piece as a new {label},{" "}
                    {onSkip ? `simply click "Skip" below.` : "simply close this window."}
                </V3BodyGrey48>
            </VerticalSpacing>
            <VerticalSpacing top={36}>
                <RightAlignPanel>
                    {modalSubmitting ? (
                        <ModalLoadingSpinner />
                    ) : onSkip ? (
                        <div>
                            <V3Button text={"Skip"} onClick={onSkip} autoWidth color={V3ButtonColor.secondary} />
                        </div>
                    ) : (
                        <div>
                            <V3Button text={"Close"} onClick={onCloseHandler} autoWidth color={V3ButtonColor.secondary} />
                        </div>
                    )}
                </RightAlignPanel>
            </VerticalSpacing>
        </>
    );
};
